// A page is kept in this browser's IndexedDB, and nowhere else. One tab at
// a time, chosen by a Web Lock, keeps it as edits arrive; every tab keeps it
// as it goes away. A save merges what is stored first, so no tab's save
// can drop another's edits.
// meets: private/local
import type { LoroDoc } from "loro-crdt";

const DB = "editor";
const PAGES = "pages";

let opened: Promise<IDBDatabase> | undefined;
function db(): Promise<IDBDatabase> {
  return (opened ??= new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(PAGES);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  }));
}

export async function load(id: string): Promise<Uint8Array | undefined> {
  const d = await db();
  return new Promise((resolve, reject) => {
    const req = d.transaction(PAGES).objectStore(PAGES).get(id);
    req.onsuccess = () => resolve(req.result ? new Uint8Array(req.result) : undefined);
    req.onerror = () => reject(req.error);
  });
}

export async function save(id: string, doc: LoroDoc): Promise<void> {
  const d = await db();
  return new Promise((resolve, reject) => {
    const tx = d.transaction(PAGES, "readwrite");
    const store = tx.objectStore(PAGES);
    const req = store.get(id);
    req.onsuccess = () => {
      if (req.result) doc.import(new Uint8Array(req.result));
      store.put(doc.export({ mode: "snapshot" }), id);
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// Drop this browser's copy of a page (one about to be taken from others).
export async function forget(id: string): Promise<void> {
  const d = await db();
  return new Promise((resolve, reject) => {
    const tx = d.transaction(PAGES, "readwrite");
    tx.objectStore(PAGES).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function pages(): Promise<string[]> {
  const d = await db();
  return new Promise((resolve, reject) => {
    const req = d.transaction(PAGES).objectStore(PAGES).getAllKeys();
    req.onsuccess = () => resolve(req.result.map(String));
    req.onerror = () => reject(req.error);
  });
}

// Keep a page while this tab is open: saves after a pause in editing if
// this tab holds the page's lock, and on the way out whether or not.
export function keep(id: string, doc: LoroDoc, pause = 300): () => void {
  let keeper = false;
  let release: () => void = () => {};
  let timer: ReturnType<typeof setTimeout> | undefined;
  navigator.locks.request(`editor:keep:${id}`, () => new Promise<void>((r) => ((keeper = true), (release = r))));
  const later = () => {
    if (!keeper) return;
    clearTimeout(timer);
    timer = setTimeout(() => save(id, doc), pause);
  };
  const stop = doc.subscribe(later);
  const now = () => void save(id, doc);
  addEventListener("pagehide", now);
  document.addEventListener("visibilitychange", () => document.visibilityState === "hidden" && now());
  // Browsers clear a site's storage under pressure unless asked not to.
  navigator.storage?.persist?.();
  return () => {
    stop();
    removeEventListener("pagehide", now);
    clearTimeout(timer);
    release();
  };
}
